import React, { useEffect, useState } from 'react';
import { teacherApi } from '../services/api';
import { dedupSubjectOptionsFromBooks } from '../utils/subjectOptions';

// Phase 13 — shared rich Hedef Ata modal so /teacher/programim and the
// Sınıflarım student-profile flow look identical. Mirror of the inline
// modal in pages/teacher/Classes.tsx with the simpler single-student
// shape. (Class-mode conflict detection still lives on the Classes page
// because that flow needs per-student schedule lookups; extracting it
// here would have made this component unnecessarily fat.)

const F = { fontFamily: 'Inter' } as const;

interface Props {
  open: boolean;
  studentUuid: string;
  studentName: string;
  // Optional defaults so the page can pre-fill the form (e.g. seeded from
  // the day the user clicked on in the weekly grid).
  defaultDate?: string;     // YYYY-MM-DD
  defaultStartTime?: string; // HH:mm
  defaultEndTime?: string;   // HH:mm
  onClose: () => void;
  onSubmitted: () => void;
}

const HedefAtaModal: React.FC<Props> = ({
  open, studentUuid, studentName,
  defaultDate, defaultStartTime, defaultEndTime,
  onClose, onSubmitted,
}) => {
  const [goalDefs, setGoalDefs] = useState<Array<{ id: number; title: string }>>([]);
  const [goalDefId, setGoalDefId] = useState<number | ''>('');
  const [target, setTarget] = useState('20');
  const [date, setDate] = useState(defaultDate ?? new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState(defaultStartTime ?? '18:00');
  const [endTime, setEndTime] = useState(defaultEndTime ?? '19:00');

  // Optional homework attachment (matches Classes.tsx Hedef Ata)
  const [attachHw, setAttachHw] = useState(false);
  const [books, setBooks] = useState<Array<{ id: number; title: string; display_name?: string | null; subjectId?: number | null; subject?: { id?: number; name?: string } | null }>>([]);
  const [bookId, setBookId] = useState<number | ''>('');
  const [sectionLabel, setSectionLabel] = useState('');
  const [sectionOptions, setSectionOptions] = useState<Array<{ section_label: string; section_display_label?: string | null; section_category?: string | null; question_count?: number }>>([]);
  const [kazanims, setKazanims] = useState<Array<{ id: number; title: string }>>([]);
  const [subjectUnitId, setSubjectUnitId] = useState<number | ''>('');

  // Standalone subject + konu selection (without book attachment)
  const [standaloneSubjectId, setStandaloneSubjectId] = useState<number | ''>('');
  const [standaloneKazanims, setStandaloneKazanims] = useState<Array<{ id: number; title: string }>>([]);
  const [standaloneUnitId, setStandaloneUnitId] = useState<number | ''>('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Reset on each open so old form state doesn't leak between students ──
  useEffect(() => {
    if (!open) return;
    setError(null);
    setAttachHw(false);
    setBookId('');
    setSectionLabel('');
    setSectionOptions([]);
    setSubjectUnitId('');
    setKazanims([]);
    setStandaloneSubjectId('');
    setStandaloneKazanims([]);
    setStandaloneUnitId('');
    setDate(defaultDate ?? new Date().toISOString().slice(0, 10));
    setStartTime(defaultStartTime ?? '18:00');
    setEndTime(defaultEndTime ?? '19:00');
  }, [open, defaultDate, defaultStartTime, defaultEndTime]);

  useEffect(() => {
    if (!open) return;
    teacherApi.goalDefinitions()
      .then((r: any) => {
        const defs = r.data ?? [];
        setGoalDefs(defs);
        if (defs.length > 0 && !goalDefId) setGoalDefId(defs[0].id);
      })
      .catch(() => setGoalDefs([]));
    // Books available to the teacher (already scoped to their branch + level).
    teacherApi.booksForAssignment()
      .then((r: any) => setBooks((r.data ?? []).map((b: any) => ({
        id: b.id,
        title: b.title,
        display_name: b.display_name,
        subjectId: b.subjectId ?? b.subject?.id ?? null,
        subject: b.subject ?? null,
      }))))
      .catch(() => setBooks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Load source-backed konular when standalone subject is picked.
  useEffect(() => {
    if (!standaloneSubjectId) {
      setStandaloneKazanims([]);
      setStandaloneUnitId('');
      return;
    }
    setStandaloneUnitId('');
    teacherApi.kazanims(Number(standaloneSubjectId))
      .then((r) => setStandaloneKazanims(r.data ?? []))
      .catch(() => setStandaloneKazanims([]));
  }, [standaloneSubjectId]);

  // Cascading: when book picked, load the book's tests + konular for its
  // subject. Resetting both on book change so a stale selection from a
  // previously-chosen book doesn't get submitted with the new one.
  useEffect(() => {
    if (!bookId) {
      setSectionOptions([]);
      setKazanims([]);
      setSectionLabel('');
      setSubjectUnitId('');
      return;
    }
    setSectionLabel('');
    setSubjectUnitId('');
    teacherApi.bookSections(bookId)
      .then((r: any) => {
        const all = r.data?.sections ?? [];
        const seen = new Set<string>();
        const opts = all.filter((s: any) => {
          if (!s.section_label || seen.has(s.section_label)) return false;
          seen.add(s.section_label);
          return true;
        }).map((s: any) => ({ section_label: s.section_label, section_display_label: s.section_display_label, section_category: s.section_category, question_count: s.question_count ?? null }));
        setSectionOptions(opts);
      })
      .catch(() => setSectionOptions([]));
    const book = books.find((b) => b.id === Number(bookId));
    const sid = book?.subjectId ?? book?.subject?.id;
    if (sid) {
      teacherApi.kazanims(Number(sid))
        .then((r) => setKazanims(r.data ?? []))
        .catch(() => setKazanims([]));
    } else {
      setKazanims([]);
    }
  }, [bookId, books]);

  if (!open) return null;

  const submit = async () => {
    setError(null);
    if (!goalDefId) { setError('Hedef türü seçin.'); return; }
    if (startTime && endTime) {
      const toMin = (t: string) => Number(t.split(':')[0]) * 60 + Number(t.split(':')[1]);
      if (toMin(endTime) <= toMin(startTime)) {
        setError('Bitiş saati başlangıç saatinden sonra olmalıdır.');
        return;
      }
    }
    setSubmitting(true);
    try {
      const homework = attachHw && bookId
        ? {
            title: goalDefs.find((g) => g.id === goalDefId)?.title ?? 'Hedef Ödevi',
            description: sectionLabel ? `Bölüm: ${sectionLabel}` : undefined,
            bookId: Number(bookId),
            sectionLabel: sectionLabel || undefined,
            dueDate: date,
          }
        : undefined;
      // Prefer book-linked konu; fall back to standalone selection.
      const sUnit = subjectUnitId ? Number(subjectUnitId) : standaloneUnitId ? Number(standaloneUnitId) : undefined;
      // AP69: hedefin dersi — standalone seçim, yoksa seçilen ödev kitabının
      // dersi. Backend tanımı ders-bazlı varyanta bağlar → kartlarda ders
      // adı (web) ve ders ikonu (mobil) görünür.
      const sSubject = standaloneSubjectId
        ? Number(standaloneSubjectId)
        : bookId
          ? (books.find((b) => b.id === Number(bookId))?.subjectId ?? undefined)
          : undefined;
      await teacherApi.assignGoalToStudent(
        studentUuid,
        Number(goalDefId),
        parseInt(target, 10) || 0,
        date, startTime, endTime,
        homework,
        sUnit,
        sSubject ?? undefined,
      );
      onSubmitted();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => !submitting && onClose()} />
      <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[560px] max-h-[90vh] overflow-y-auto p-6">
        <button onClick={() => !submitting && onClose()} className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-200 flex items-center justify-center" aria-label="Kapat">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
            <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
          </svg>
        </button>
        <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#3973bf', marginBottom: 4 }}>
          {studentName} — Hedef Ata
        </h2>
        <p style={{ ...F, fontSize: 13, color: '#666', marginBottom: 18 }}>
          Hedef öğrencinin programına eklenir. İsterseniz bir kitap ödevi de bağlayabilirsiniz.
        </p>

        <div className="mb-3">
          <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Hedef Türü</label>
          <select value={goalDefId} onChange={(e) => setGoalDefId(e.target.value ? Number(e.target.value) : '')}
            className="w-full bg-white rounded-[12px] px-4 py-2.5 outline-none text-[14px]"
            style={{ border: '2px solid #3973bf', ...F }}>
            {goalDefs.length === 0 && <option value="">Yükleniyor…</option>}
            {goalDefs.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
          </select>
        </div>
        <div className="mb-3">
          <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Hedef Soru Sayısı</label>
          <input type="number" min={1} max={500} value={target} onChange={(e) => setTarget(e.target.value)}
            className="w-full bg-white rounded-[12px] px-4 py-2.5 outline-none text-[16px] font-bold text-center"
            style={{ border: '2px solid #3973bf', ...F }} />
        </div>
        <div className="mb-3">
          <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Tarih</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full bg-white rounded-[12px] px-4 py-2.5 outline-none text-[14px]"
            style={{ border: '2px solid #3973bf', ...F }} />
        </div>
        <div className="grid grid-cols-2 gap-2 mb-5">
          <div>
            <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Başlangıç</label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]"
              style={{ border: '2px solid #3973bf', ...F }} />
          </div>
          <div>
            <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Bitiş</label>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
              className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]"
              style={{ border: '2px solid #3973bf', ...F }} />
          </div>
        </div>

        {/* Standalone ders + konu seçimi (kitap olmadan) */}
        {(() => {
          // AP57: aynı ders iki kez görünüyordu (Fizik + Physics, Matematik ×2 —
          // subjects tablosundaki mükerrer satırların ikisinin de kitabı var).
          // Kanonik ada göre gruplanır; temsilci id en çok kitaplı satırdır.
          const subjectOptions = dedupSubjectOptionsFromBooks(books);
          // AP60: seçenek yokken bölüm GİZLENMEZ — eskiden sessizce kaybolup
          // "Ders & Konu seçimi gitti" görünümü veriyordu (kök: books endpoint'i
          // sınıfsız çağrıda 0 dönüyordu; backend'de düzeltildi). Boşsa devre
          // dışı select + açıklama gösterilir.
          const noSubjects = subjectOptions.length === 0;
          return (
            <div className="mb-4 rounded-[12px] p-3" style={{ background: '#f4f6fa', border: '1.5px solid #e1e7f3' }}>
              <p style={{ ...F, fontSize: 13, fontWeight: 700, color: '#3973bf', marginBottom: 8 }}>
                Ders & Konu (opsiyonel)
              </p>
              <div className="space-y-2">
                <div>
                  <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Ders</label>
                  <select
                    value={standaloneSubjectId}
                    onChange={(e) => setStandaloneSubjectId(e.target.value ? Number(e.target.value) : '')}
                    disabled={noSubjects}
                    className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px] disabled:opacity-60"
                    style={{ border: '1.5px solid #3973bf', ...F }}
                  >
                    <option value="">{noSubjects ? 'Atanabilir kitap/ders bulunamadı' : 'Ders seçilmedi'}</option>
                    {subjectOptions.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  {noSubjects && (
                    <p style={{ ...F, fontSize: 11, color: '#8d8d8d', marginTop: 4 }}>
                      Branşınıza uygun kitap bulunamadığı için ders listesi boş — branş/kitap tanımlarını kontrol edin.
                    </p>
                  )}
                </div>
                <div>
                  <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>
                    Konu {standaloneSubjectId ? '(opsiyonel)' : '— önce ders seçin'}
                  </label>
                  <select
                    value={standaloneUnitId}
                    onChange={(e) => setStandaloneUnitId(e.target.value ? Number(e.target.value) : '')}
                    disabled={!standaloneSubjectId || standaloneKazanims.length === 0}
                    className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px] disabled:opacity-60"
                    style={{ border: '1.5px solid #d8d8d8', ...F }}
                  >
                    <option value="">Konu seçilmedi</option>
                    {standaloneKazanims.map(k => (
                      <option key={k.id} value={k.id}>{k.title}</option>
                    ))}
                  </select>
                </div>
                {standaloneSubjectId && standaloneUnitId && (
                  <p style={{ ...F, fontSize: 11, color: '#3973bf', fontWeight: 600 }}>
                    Hedef şu şekilde görünecek: {subjectOptions.find(s => s.id === Number(standaloneSubjectId))?.name} — {standaloneKazanims.find(k => k.id === Number(standaloneUnitId))?.title}
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Optional homework — same layout as Sınıflarım hedef ata */}
        <div className="mb-4 rounded-[12px] p-3" style={{ background: '#f4f6fa', border: '1.5px solid #e1e7f3' }}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={attachHw} onChange={(e) => setAttachHw(e.target.checked)} />
            <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>
              Kitap ödevi de oluştur
            </span>
          </label>
          {attachHw && (
            <div className="mt-3 space-y-2">
              <div>
                <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Kitap</label>
                <select
                  value={bookId}
                  onChange={(e) => setBookId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px]"
                  style={{ border: '1.5px solid #3973bf', ...F }}
                >
                  <option value="">Kitap seçin</option>
                  {books.map((b) => (
                    <option key={b.id} value={b.id}>{b.display_name ?? b.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>
                  Test / Bölüm {bookId ? '(opsiyonel)' : '— önce kitap seçin'}
                </label>
                <select
                  value={sectionLabel}
                  onChange={(e) => {
                    const label = e.target.value;
                    setSectionLabel(label);
                    // Auto-fill target question count from the section's question_count
                    const sec = sectionOptions.find(s => s.section_label === label);
                    if (sec?.question_count) setTarget(String(sec.question_count));
                  }}
                  disabled={!bookId || sectionOptions.length === 0}
                  className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px] disabled:opacity-60"
                  style={{ border: '1.5px solid #d8d8d8', ...F }}
                >
                  <option value="">Tüm Kitap (test seçilmedi)</option>
                  {sectionOptions.map((s) => (
                    <option key={s.section_label} value={s.section_label}>
                      {s.section_display_label || s.section_label}{s.section_category ? ` — ${s.section_category}` : ''}{s.question_count ? ` (${s.question_count} soru)` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>
                  Konu {bookId ? '(opsiyonel)' : '— önce kitap seçin'}
                </label>
                <select
                  value={subjectUnitId}
                  onChange={(e) => setSubjectUnitId(e.target.value ? Number(e.target.value) : '')}
                  disabled={!bookId || kazanims.length === 0}
                  className="w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[13px] disabled:opacity-60"
                  style={{ border: '1.5px solid #d8d8d8', ...F }}
                >
                  <option value="">Konu seçilmedi</option>
                  {kazanims.map((k) => (
                    <option key={k.id} value={k.id}>{k.title}</option>
                  ))}
                </select>
              </div>
              <p style={{ ...F, fontSize: 11, color: '#888' }}>
                Ödev hem Çalışma Planı hem de Ödevlerim sayfasında görünür.
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-[10px] px-4 py-2 text-[13px] text-[#c73d74]" style={{ background: '#fdecef' }}>{error}</div>
        )}

        <div className="flex gap-3">
          <button onClick={() => !submitting && onClose()}
            className="flex-1 rounded-[12px] py-2.5 hover:opacity-90 disabled:opacity-50" style={{ background: '#e5e5e5' }}>
            <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>İptal</span>
          </button>
          <button onClick={submit} disabled={submitting}
            className="flex-1 rounded-[12px] py-2.5 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2" style={{ background: '#3973bf' }}>
            {!submitting && <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" /></svg>}
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>
              {submitting ? 'Atanıyor…' : 'Hedef Ata'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HedefAtaModal;
